import { forwardRef, useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import "./DateDropdown.scss";
import "../Dropdown.scss";
import Button from "../../ui/Button/Button";
import { useSelector } from "react-redux";
import { formatDateRangeDisplay } from "../../../reducers/utils";

// German holidays and school vacation data
const holidayData = {
  publicHolidays: {
    "2025": [
      {date: "2025-01-01", name: "Neujahr", states: ["ALL"]},
      {date: "2025-01-06", name: "Heilige Drei Könige", states: ["BW", "BY", "ST"]},
      {date: "2025-04-18", name: "Karfreitag", states: ["ALL"]},
      {date: "2025-04-21", name: "Ostermontag", states: ["ALL"]},
      {date: "2025-05-01", name: "Tag der Arbeit", states: ["ALL"]},
      {date: "2025-10-03", name: "Tag der Deutschen Einheit", states: ["ALL"]},
      {date: "2025-12-25", name: "1. Weihnachtsfeiertag", states: ["ALL"]},
      {date: "2025-12-26", name: "2. Weihnachtsfeiertag", states: ["ALL"]}
    ],
    "2026": [
      {date: "2026-01-01", name: "Neujahr", states: ["ALL"]},
      {date: "2026-04-03", name: "Karfreitag", states: ["ALL"]},
      {date: "2026-04-06", name: "Ostermontag", states: ["ALL"]},
      {date: "2026-05-01", name: "Tag der Arbeit", states: ["ALL"]},
      {date: "2026-10-03", name: "Tag der Deutschen Einheit", states: ["ALL"]},
      {date: "2026-12-25", name: "1. Weihnachtsfeiertag", states: ["ALL"]},
      {date: "2026-12-26", name: "2. Weihnachtsfeiertag", states: ["ALL"]}
    ]
  },
  schoolHolidays: {
    "BE": [
      {start: "2025-02-03", end: "2025-02-08", name: "Winterferien"},
      {start: "2025-04-14", end: "2025-04-25", name: "Osterferien"},
      {start: "2025-07-24", end: "2025-09-06", name: "Sommerferien"},
      {start: "2026-02-02", end: "2026-02-07", name: "Winterferien"}
    ],
    "BY": [
      {start: "2025-04-14", end: "2025-04-26", name: "Osterferien"},
      {start: "2025-08-01", end: "2025-09-15", name: "Sommerferien"}
    ],
    "NW": [
      {start: "2025-04-14", end: "2025-04-26", name: "Osterferien"},
      {start: "2025-07-14", end: "2025-08-26", name: "Sommerferien"}
    ]
  },
  states: [
    {code: "BE", name: "Berlin"},
    {code: "BY", name: "Bayern"},
    {code: "NW", name: "Nordrhein-Westfalen"},
    {code: "HH", name: "Hamburg"},
    {code: "BW", name: "Baden-Württemberg"}
  ]
};

const DateDropdown = forwardRef(({ onSelect, onClose, selectedValue }, ref) => {
  const intl = useIntl();
  const today = new Date();
  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );
  const isACC = useSelector((state) => state.search.activeMode) === "ACC";

  const [duration, setDuration] = useState(selectedValue?.value?.duration || 7);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [customDuration, setCustomDuration] = useState("");
  const [selectedState, setSelectedState] = useState("BE"); // Berlin default

  const [view, setView] = useState("calendar");

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split(".");
    if (parts.length !== 3) return null;
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDefaultStartDate = () => {
    if (selectedValue?.value?.start) {
      return selectedValue.value.start;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(selectedValue?.value?.end || "");

  const calculateDaysBetween = (start, end) => {
    if (!start || !end) return 0;
    const startDateObj = parseDate(start);
    const endDateObj = parseDate(end);
    if (!startDateObj || !endDateObj) return 0;
    const diffTime = endDateObj - startDateObj;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const maxDays = calculateDaysBetween(startDate, endDate);

  useEffect(() => {
    if (selectedValue?.value) {
      setStartDate(selectedValue.value.start || getDefaultStartDate());
      setEndDate(selectedValue.value.end || "");
      const savedDuration = selectedValue.value.duration || 7;
      setDuration(savedDuration);

      const presetDurations = [5, 7, 10, 14, 21];
      if (!presetDurations.includes(savedDuration)) {
        setCustomDuration(savedDuration.toString());
      }
    }
  }, [selectedValue]);

  useEffect(() => {
    if (startDate && endDate) {
      const days = calculateDaysBetween(startDate, endDate);
      if (days > 0 && duration > days) {
        setDuration(days);
        setCustomDuration(days.toString());
      }
    }
  }, [startDate, endDate]);

  const handleDateSelect = () => {
    const dateRange = formatDateRangeDisplay(startDate, endDate, duration, null, isACC, intl);

    const weekPreset = isACC
      ? null
      : !customDuration && duration === 7
      ? "week"
      : !customDuration && duration === 14
      ? "twoWeeks"
      : !customDuration && duration === 21
      ? "threeWeeks"
      : null;

    onSelect({
      label: dateRange,
      value: { start: startDate, end: endDate, duration: duration, weekPreset },
    });
    if (onClose) {
      onClose();
    }
  };

  // Holiday checking functions
  const isPublicHoliday = useMemo(() => (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const year = date.getFullYear().toString();
    const holidays = holidayData.publicHolidays[year] || [];

    return holidays.some(holiday => {
      if (holiday.date === dateStr) {
        return holiday.states.includes("ALL") || holiday.states.includes(selectedState);
      }
      return false;
    });
  }, [selectedState]);

  const isSchoolHoliday = useMemo(() => (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const schoolHolidays = holidayData.schoolHolidays[selectedState] || [];

    return schoolHolidays.some(holiday => {
      return dateStr >= holiday.start && dateStr <= holiday.end;
    });
  }, [selectedState]);

  const generateCalendarDays = (monthOffset = 0) => {
    const month = currentMonth + monthOffset;
    const year = currentYear + Math.floor(month / 12);
    const adjustedMonth = month % 12;

    const firstDay = new Date(year, adjustedMonth, 1);
    const lastDay = new Date(year, adjustedMonth + 1, 0);

    const startDay = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDay.setDate(startDay.getDate() - daysFromMonday);

    const days = [];
    const current = new Date(startDay);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDayClick = (date) => {
    if (isDateDisabled(date)) return;

    const formattedDate = formatDate(date);

    if (!startDate || (startDate && endDate)) {
      setStartDate(formattedDate);
      setEndDate("");
    } else if (startDate && !endDate) {
      const startDateObj = parseDate(startDate);
      if (date >= startDateObj) {
        setEndDate(formattedDate);
      } else {
        setStartDate(formattedDate);
        setEndDate("");
      }
    }
  };

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const getMonthNames = () => [
    intl.formatMessage({ id: "january" }),
    intl.formatMessage({ id: "february" }),
    intl.formatMessage({ id: "march" }),
    intl.formatMessage({ id: "april" }),
    intl.formatMessage({ id: "may" }),
    intl.formatMessage({ id: "june" }),
    intl.formatMessage({ id: "july" }),
    intl.formatMessage({ id: "august" }),
    intl.formatMessage({ id: "september" }),
    intl.formatMessage({ id: "october" }),
    intl.formatMessage({ id: "november" }),
    intl.formatMessage({ id: "december" }),
  ];

  const getWeekdays = () => [
    intl.formatMessage({ id: "monday" }),
    intl.formatMessage({ id: "tuesday" }),
    intl.formatMessage({ id: "wednesday" }),
    intl.formatMessage({ id: "thursday" }),
    intl.formatMessage({ id: "friday" }),
    intl.formatMessage({ id: "saturday" }),
    intl.formatMessage({ id: "sunday" }),
  ];

  const monthNames = getMonthNames();
  const weekdays = getWeekdays();

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    return date >= startDateObj && date <= endDateObj;
  };

  const handleDateInput = (value, type) => {
    const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
    if (dateRegex.test(value)) {
      const parsedDate = parseDate(value);
      if (parsedDate && !isDateDisabled(parsedDate)) {
        if (type === "start") {
          setStartDate(value);
          if (endDate) {
            const endDateObj = parseDate(endDate);
            if (parsedDate > endDateObj) {
              setEndDate("");
            }
          }
        } else {
          const startDateObj = parseDate(startDate);
          if (parsedDate >= startDateObj) {
            setEndDate(value);
          }
        }
      }
    } else {
      if (type === "start") {
        setStartDate(value);
      } else {
        setEndDate(value);
      }
    }
  };

  const handleCustomDurationChange = (value) => {
    setCustomDuration(value);
    const numValue = parseInt(value, 10);

    if (!isNaN(numValue) && numValue > 0) {
      if (maxDays > 0 && numValue > maxDays) {
        setDuration(maxDays);
        setCustomDuration(maxDays.toString());
      } else {
        setDuration(numValue);
      }
    }
  };

  const handleDurationSelect = (days) => {
    setDuration(days);
    setCustomDuration("");
  };

  const shouldShowDurationOption = (days) => {
    if (!startDate || !endDate) return true;
    return maxDays >= days;
  };

  return (
    <div className="main_input-dropdown date-dropdown" ref={ref}>
      <div className="view-tabs">
        <div
          className={`view-tab ${view === "calendar" ? "active" : ""}`}
          onClick={() => setView("calendar")}
        >
          {intl.formatMessage({ id: "calendar" })}
        </div>
        <div
          className={`view-tab ${view === "duration" ? "active" : ""}`}
          onClick={() => setView("duration")}
        >
          {intl.formatMessage({ id: "duration" })}
        </div>
      </div>

      {view === "calendar" && (
        <div className="date-picker">
          <div className="calendar-header">
            <button
              className="nav-button"
              onClick={() => navigateMonth("prev")}
              type="button"
            >
              ‹
            </button>
            <div className="month-headers">
              <span className="month-year">
                {monthNames[currentMonth]} {currentYear}
              </span>
              <span className="separator">|</span>
              <span className="month-year">
                {monthNames[(currentMonth + 1) % 12]} {currentMonth === 11 ? currentYear + 1 : currentYear}
              </span>
            </div>
            <button
              className="nav-button"
              onClick={() => navigateMonth("next")}
              type="button"
            >
              ›
            </button>
          </div>

          {/* Bundesland Selector */}
          <div className="holiday-filter">
            <label>{intl.formatMessage({ id: "selectState" }) || "Bundesland auswählen"}</label>
            <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)}>
              {holidayData.states.map(state => (
                <option key={state.code} value={state.code}>{state.name}</option>
              ))}
            </select>
          </div>

          {/* Dual-Month Calendar Container */}
          <div className="dual-calendar-container">
            {/* First Month */}
            <div className="calendar-grid">
              <div className="weekdays">
                {weekdays.map((day) => (
                  <div key={day} className="weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="days">
                {generateCalendarDays(0).map((date, index) => {
                  const formattedDate = formatDate(date);
                  const isStart = formattedDate === startDate;
                  const isEnd = formattedDate === endDate;
                  const isInRange = isDateInRange(date);
                  const isDisabled = isDateDisabled(date);
                  const isCurrentMonth = date.getMonth() === currentMonth;
                  const isPubHoliday = isPublicHoliday(date);
                  const isSchHoliday = isSchoolHoliday(date);

                  return (
                    <div
                      key={index}
                      className={`day ${!isCurrentMonth ? "other-month" : ""} ${
                        isStart ? "selected start" : ""
                      } ${isEnd ? "selected end" : ""} ${
                        isInRange && !isStart && !isEnd ? "in-range" : ""
                      } ${isDisabled ? "disabled" : ""} ${
                        isPubHoliday ? "public-holiday" : ""
                      } ${isSchHoliday ? "school-holiday" : ""}`}
                      onClick={() => !isDisabled && handleDayClick(date)}
                      title={isPubHoliday ? "Feiertag" : isSchHoliday ? "Schulferien" : ""}
                    >
                      {date.getDate()}
                      {isPubHoliday && <span className="holiday-indicator">•</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Second Month */}
            <div className="calendar-grid">
              <div className="weekdays">
                {weekdays.map((day) => (
                  <div key={`${day}-2`} className="weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="days">
                {generateCalendarDays(1).map((date, index) => {
                  const formattedDate = formatDate(date);
                  const isStart = formattedDate === startDate;
                  const isEnd = formattedDate === endDate;
                  const isInRange = isDateInRange(date);
                  const isDisabled = isDateDisabled(date);
                  const nextMonth = (currentMonth + 1) % 12;
                  const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
                  const isCurrentMonth = date.getMonth() === nextMonth && date.getFullYear() === nextYear;
                  const isPubHoliday = isPublicHoliday(date);
                  const isSchHoliday = isSchoolHoliday(date);

                  return (
                    <div
                      key={index}
                      className={`day ${!isCurrentMonth ? "other-month" : ""} ${
                        isStart ? "selected start" : ""
                      } ${isEnd ? "selected end" : ""} ${
                        isInRange && !isStart && !isEnd ? "in-range" : ""
                      } ${isDisabled ? "disabled" : ""} ${
                        isPubHoliday ? "public-holiday" : ""
                      } ${isSchHoliday ? "school-holiday" : ""}`}
                      onClick={() => !isDisabled && handleDayClick(date)}
                      title={isPubHoliday ? "Feiertag" : isSchHoliday ? "Schulferien" : ""}
                    >
                      {date.getDate()}
                      {isPubHoliday && <span className="holiday-indicator">•</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="date-inputs">
            <div className="date-input-group">
              <label>{intl.formatMessage({ id: "arrival" })}</label>
              <input
                type="text"
                value={startDate}
                onChange={(e) => handleDateInput(e.target.value, "start")}
                placeholder="DD.MM.YYYY"
              />
            </div>
            <div className="date-input-group">
              <label>{intl.formatMessage({ id: "departure" })}</label>
              <input
                type="text"
                value={endDate}
                onChange={(e) => handleDateInput(e.target.value, "end")}
                placeholder="DD.MM.YYYY"
              />
            </div>
          </div>

          <Button
            text={intl.formatMessage({ id: "next" })}
            style={"rounded"}
            color={"orange"}
            size={"m"}
            onClick={() => setView("duration")}
          />
        </div>
      )}

      {view === "duration" && (
        <div className="duration-selector">
          <div className="duration-header">
            <h4>{intl.formatMessage({ id: "selectDuration" })}</h4>
          </div>
          <div className="duration-options">
            {shouldShowDurationOption(5) && (
              <div
                className={`duration-option ${
                  duration === 5 && !customDuration ? "selected" : ""
                }`}
                onClick={() => handleDurationSelect(5)}
              >
                <span className="duration-value">5</span>
                <span className="duration-label">
                  {intl.formatMessage({ id: "days" })}
                </span>
              </div>
            )}
            {shouldShowDurationOption(7) && (
              <div
                className={`duration-option ${
                  duration === 7 && !customDuration ? "selected" : ""
                }`}
                onClick={() => handleDurationSelect(7)}
              >
                <span className="duration-value">1</span>
                <span className="duration-label">
                  {intl.formatMessage({ id: "week" })}
                </span>
              </div>
            )}
            {shouldShowDurationOption(10) && (
              <div
                className={`duration-option ${
                  duration === 10 && !customDuration ? "selected" : ""
                }`}
                onClick={() => handleDurationSelect(10)}
              >
                <span className="duration-value">10</span>
                <span className="duration-label">
                  {intl.formatMessage({ id: "days" })}
                </span>
              </div>
            )}
            {shouldShowDurationOption(14) && (
              <div
                className={`duration-option ${
                  duration === 14 && !customDuration ? "selected" : ""
                }`}
                onClick={() => handleDurationSelect(14)}
              >
                <span className="duration-value">2</span>
                <span className="duration-label">
                  {intl.formatMessage({ id: "weeks" })}
                </span>
              </div>
            )}
            {shouldShowDurationOption(21) && (
              <div
                className={`duration-option ${
                  duration === 21 && !customDuration ? "selected" : ""
                }`}
                onClick={() => handleDurationSelect(21)}
              >
                <span className="duration-value">3</span>
                <span className="duration-label">
                  {intl.formatMessage({ id: "weeks" })}
                </span>
              </div>
            )}
          </div>

          <div className="custom-duration-input">
            <label>{intl.formatMessage({ id: "customDuration" })}</label>
            <input
              type="number"
              min="1"
              max={maxDays || 365}
              value={customDuration}
              onChange={(e) => handleCustomDurationChange(e.target.value)}
              placeholder={maxDays > 0 ? `1-${maxDays}` : "1-365"}
            />
            {maxDays > 0 && (
              <span className="max-days-hint">
                {intl.formatMessage({ id: "maxDays" })}: {maxDays}{" "}
                {intl.formatMessage({ id: "days" })}
              </span>
            )}
          </div>

          <Button
            text={intl.formatMessage({ id: "applyDate" })}
            style={"rounded"}
            color={"orange"}
            size={"m"}
            onClick={() => handleDateSelect()}
          />
        </div>
      )}
    </div>
  );
});

export default DateDropdown;
